<template>
    <div class="chart-wrapper">
        <div class="chart-header">
            <h5 class="chart-title">{{ title }}</h5>
        </div>
        <div class="card p-0">
            <div v-if="isLoading" key="spinner" class="info-icon">
                <div class="spinner">
                    <div class="spinner-border spinner-border-sm text-primary" role="status">
                        <span class="sr-only">Cargando...</span>
                    </div>
                </div>
            </div>
            <div v-else-if="showInfoButton" key="info-button" class="info-icon">
                <button class="btn info-btn" @click="handleInfoClick">
                    <i class="fa fa-info-circle"></i>
                </button>
            </div>
            <div class="card-body p-0">
                <div v-if="hasData" ref="chart" style="height: 300px; width: 100%;"></div>
                <div v-else class="no-data-message">
                    <p>No hay datos disponibles para mostrar el gráfico.</p>
                </div>

                <!-- Controles de paginación -->
                <div v-if="showPagination" class="pagination-controls mt-3">
                    <button class="btn btn-light btn-sm mr-2" :disabled="currentPage === 1"
                        @click="changePage(currentPage - 1)">
                        <i class="fa fa-chevron-left"></i>
                    </button>
                    <span class="mx-2">
                        Página {{ currentPage }} de {{ totalPages }}
                    </span>
                    <button class="btn btn-light btn-sm ml-2" :disabled="currentPage === totalPages"
                        @click="changePage(currentPage + 1)">
                        <i class="fa fa-chevron-right"></i>
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import * as echarts from 'echarts'

export default {
    name: 'ChartCard',
    props: {
        title: {
            type: String,
            required: true
        },
        options: {
            type: Object,
            required: true
        },
        isPaginated: {
            type: Boolean,
            default: false
        },
        fullData: {
            type: Array,
            default: () => []
        },
        itemsPerPage: {
            type: Number,
            default: 10
        },
        chartDescription: {
            type: String,
            default: 'Información detallada sobre el gráfico.'
        },
        chartKey: {
            type: String,
            required: true
        },
        showInfoButton: {
            type: Boolean,
            default: true
        },
        isLoading: { type: Boolean, default: false },
    },
    data() {
        return {
            currentPage: 1,
            chart: null,
            showInfoIcon: false,
        }
    },
    computed: {
        hasData() {
            return this.options && this.options.series &&
                this.options.series.some(series => series.data && series.data.length > 0);
        },
        showPagination() {
            return this.isPaginated && this.fullData.length > this.itemsPerPage;
        },
        totalPages() {
            return Math.ceil(this.fullData.length / this.itemsPerPage);
        }
    },
    methods: {
        async handleInfoClick() {
                await this.$emit('chart-info-click', {
                    key: this.chartKey,
                    title: this.title
                });
        },
        initChart() {
            if (!this.chart) {
                this.chart = echarts.init(this.$refs.chart);
            }
            this.chart.setOption(this.options);
            window.addEventListener('resize', this.handleResize);
        },
        handleResize() {
            if (this.chart) {
                this.chart.resize();
            }
        },
        changePage(newPage) {
            this.currentPage = newPage;
            this.$emit('page-changed', newPage);
        }
    },
    watch: {
        options: {
            handler(newVal) {
                if (this.hasData && this.chart) {
                    this.chart.setOption(newVal);
                }
            },
            deep: true
        }
    },
    mounted() {
        if (this.hasData) {
            this.initChart();
        }
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.handleResize);
        if (this.chart) {
            this.chart.dispose();
            this.chart = null;
        }
    }
}
</script>

<style scoped>
.chart-wrapper {
    position: relative;
}

.chart-header {
    display: flex;
    align-items: center;
    margin-bottom: 0.75rem;
}

.chart-title {
    position: relative;
    margin-bottom: 0;
    font-size: 18px;
    font-weight: 800;
    color: #1B212E;
    z-index: 1;
}

.card {
    position: relative;
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 25px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    overflow: hidden;
}

.info-icon {
    position: absolute;
    top: 10px;
    right: 10px;
    z-index: 10;
}

.info-btn {
    width: 32px;
    height: 32px;
    padding: 0;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.9);
    border: 1px solid #e2e8f0;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
}

.info-btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    background: white;
}

.info-btn i {
    color: #4a5568;
    font-size: 1.1rem;
    transition: color 0.3s ease;
}

.info-btn:hover i {
    color: #2d3748;
}

.spinner {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.9);
    border-radius: 50%;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.spinner-border {
    width: 1.2rem;
    height: 1.2rem;
    border-width: 0.15em;
}

/* Transición suave para el icono de información */
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease, transform 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
    transform: scale(0.95);
}

.card-body {
    padding: 0.5rem !important;
    font-family: 'Poppins', sans-serif;
    font-weight: 300;
    color: #1B212E;
}

.no-data-message {
    text-align: center;
    color: #718096;
    padding: 3rem 0;
}

.pagination-controls {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 1rem;
}

.btn-light {
    background-color: #f8f9fa;
    border-color: #e2e8f0;
    transition: all 0.2s ease;
}

.btn-light:hover:not(:disabled) {
    background-color: #e9ecef;
    transform: translateY(-1px);
}

.btn-light:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.mx-2 {
    color: #4a5568;
    font-size: 0.875rem;
}
</style>
